import { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

/**
 * Parses the VITE_ALLOWED_API_HOSTS env var (comma-separated) and merges
 * with hosts derived from VITE_APP_BASE_URL and VITE_APP_BASE_NWL_URL.
 * Returns a deduplicated array of allowed hostnames.
 */
function getAllowedHosts(): string[] {
  const hosts = new Set<string>();

  // 1. Explicit allowlist from env var (comma-separated)
  const envHosts = import.meta.env.VITE_ALLOWED_API_HOSTS as string | undefined;
  if (envHosts) {
    envHosts
      .split(',')
      .map((h) => h.trim().toLowerCase())
      .filter(Boolean)
      .forEach((h) => hosts.add(h));
  }

  // 2. Derive hosts from the base URLs already configured
  const baseUrls = [
    import.meta.env.VITE_APP_BASE_URL,
    import.meta.env.VITE_APP_BASE_NWL_URL,
  ].filter(Boolean) as string[];

  for (const raw of baseUrls) {
    try {
      const { hostname } = new URL(raw);
      hosts.add(hostname.toLowerCase());
    } catch {
      // skip malformed URLs
    }
  }

  return Array.from(hosts);
}

/**
 * Adds a request interceptor to the given Axios instance that blocks
 * any request whose resolved URL points to a host not in the allowlist.
 *
 * Relative URLs (the normal case) are resolved against the instance's
 * baseURL, so they will always match. Only absolute URLs that point to
 * an unexpected domain will be rejected.
 */
export function addOriginGuard(axiosInstance: AxiosInstance): void {
  const allowedHosts = getAllowedHosts();

  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      try {
        const fullUrl = new URL(config.url ?? '', config.baseURL);
        const target = fullUrl.hostname.toLowerCase();

        if (!allowedHosts.includes(target)) {
          console.error(
            `[OriginGuard] Blocked request to unauthorized host: ${target}. Allowed: ${allowedHosts.join(', ')}`,
          );
          return Promise.reject(
            new Error(
              `[OriginGuard] Request blocked — host "${target}" is not in the allowed list.`,
            ),
          );
        }
      } catch {
        // If URL parsing fails, let the request through — axios will
        // handle the invalid URL downstream.
      }

      return config;
    },
  );
}
